import { Array, Option } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as Atom from 'effect/unstable/reactivity/Atom';

import type { Cart } from '../../capability/cart/cartSchema';
import {
  makeOptimisticCartState,
  makePendingQuantityAtoms,
  makeVisiblePendingCartLinesAtom,
} from './addToCart';

export const quantityOfVariantInCart = (cart: Cart, variantCode: string): number =>
  Array.reduce(cart.items, 0, (quantity, item) =>
    item.variantCode === variantCode ? quantity + item.quantity : quantity
  );

const cartUnitCount = (cart: Cart): number =>
  Array.reduce(cart.items, 0, (quantity, item) => quantity + item.quantity);

const variantStatus = (knownCart: Option.Option<Cart>, pending: number, variantCode: string) => ({
  quantityInCart:
    Option.match(knownCart, {
      onNone: () => 0,
      onSome: (cart) => quantityOfVariantInCart(cart, variantCode),
    }) + pending,
  isAdding: pending > 0,
});

/** The cart state that both browser storefronts render and mutate. */
export const makeCartState = <E>(
  cartAtom: Atom.Atom<AsyncResult.AsyncResult<Option.Option<Cart>, E>>
) => {
  const optimistic = makeOptimisticCartState();
  const knownCartAtom = Atom.make((get) => {
    const knownCart = Option.flatten(AsyncResult.value(get(cartAtom)));
    const pendingLines = get(optimistic.pendingCartLinesAtom);
    return Option.filter(
      knownCart,
      (cart) =>
        !pendingLines.some(
          (line) =>
            Option.isSome(line.confirmationCartToken) &&
            line.confirmationCartToken.value !== cart.token
        )
    );
  });
  const visiblePendingCartLinesAtom = makeVisiblePendingCartLinesAtom(
    cartAtom,
    quantityOfVariantInCart,
    optimistic.pendingCartLinesAtom
  );
  const pendingQuantities = makePendingQuantityAtoms(visiblePendingCartLinesAtom);
  const projectedUnitCountAtom = Atom.make((get) =>
    Option.match(get(knownCartAtom), {
      onNone: () => get(pendingQuantities.pendingQuantityAtom),
      onSome: (cart) => cartUnitCount(cart) + get(pendingQuantities.pendingQuantityAtom),
    })
  );
  const variantStatusAtom = Atom.family((variantCode: string) =>
    Atom.make((get) =>
      variantStatus(
        get(knownCartAtom),
        get(pendingQuantities.pendingQuantityByVariantCodeAtom(variantCode)),
        variantCode
      )
    )
  );
  const variantStatusLookupAtom = Atom.make((get) => {
    const knownCart = get(knownCartAtom);
    const pendingLines = get(visiblePendingCartLinesAtom);
    return (variantCode: string) =>
      variantStatus(
        knownCart,
        Array.reduce(pendingLines, 0, (sum, line) =>
          line.variantCode === variantCode ? sum + line.quantity : sum
        ),
        variantCode
      );
  });

  return {
    cartAtom,
    knownCartAtom,
    visiblePendingCartLinesAtom,
    projectedUnitCountAtom,
    variantStatusAtom,
    variantStatusLookupAtom,
    ...optimistic,
    ...pendingQuantities,
  };
};
